import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { dataGridColumns } from '../../utils/helper';
import productionService from '../../service/production.service';
import { DataGridUI, DataGridIdentifiers, ActionButtonUI, ButtonUI } from '../Interface';
import { GridRowModes } from '@mui/x-data-grid';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import History from '../History/History';
import { Box, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

export const OperationReportTable = ({
  productionList,
  refetch,
  setHistoryDialogOpen,
  setSelectedRow,
  setReportAttachmentDialog,
}) => {
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const { refetchTable, setRefetchTable } = refetch;
  const [rowModesModel, setRowModesModel] = useState({});

  const [query, setQuery] = useState({
    filter: [],
    sorting: {
      sortModel: [{ field: 'date', sort: 'desc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
  });

  useEffect(() => {
    if (productionList?.visOrder && Object.keys(query).length > 0) {
      getProductionReports(productionList.prodOperationId);
    }
  }, [query, productionList]);

  useEffect(() => {
    if (refetchTable && query.filter?.length > 0 && productionList?.prodOperationId) {
      getProductionReports(productionList.prodOperationId);
    }
  }, [refetchTable, productionList]);

  const onPaginationChange = (paginationModel) => {
    setQuery((prev) => {
      return { ...prev, pagination: { paginationModel } };
    });
  };

  const handleSortModelChange = (sortModel) => {
    setQuery((prev) => {
      const paginationModel = {
        pageSize: prev.pagination.paginationModel.pageSize,
        page: 0,
      };

      return {
        ...prev,
        sorting: { sortModel },
        pagination: { paginationModel },
      };
    });
  };

  useEffect(() => {
    if (productionList?.prodOperationId) {
      getProductionReports(productionList.prodOperationId);
    } else {
      setReports({ rows: [], rowCount: 0 });
    }
  }, [productionList, refetchTable]);

  const getProductionReports = (prodOperationId) => {
    productionService.getProductionReports(prodOperationId, query).then((data) => {
      setReports(data);
      setRefetchTable(false);
    });
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleDeleteClick = (id) => () => {
    productionService.deleteProductionReport(id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
    });
  };

  const processRowUpdate = async (newRow) => {
    await productionService.simpleUpdateProdReport(newRow);
    setRefetchTable(true);
    return newRow;
  };

  const columns = dataGridColumns([
    {
      field: 'id',
      headerName: 'Sorszám',
      flex: 1,
    },
    {
      field: 'date',
      headerName: 'Időpont',
      flex: 1,
      valueGetter: (data) => {
        return dayjs(data.row.date).format('YYYY.MM.DD. HH:mm');
      },
    },
    {
      field: 'userId',
      headerName: 'Dolgozó',
      flex: 1,
      valueGetter: (data) => {
        return data.row.user?.nickName;
      },
    },
    {
      field: 'timeSpentInMin',
      headerName: 'Idő ráfordítás (perc)',
      flex: 1,
      editable: true,
    },
    {
      field: 'quantity',
      headerName: 'Mennyiség',
      flex: 1,
      editable: true,
    },
    {
      field: 'scrapQuantity',
      headerName: 'Selejt mennyiség',
      flex: 1,
      editable: true,
    },
    {
      field: 'scrapReasonCode',
      headerName: 'Selejt ok',
      flex: 1,
      valueGetter: (data) => {
        return data.row.scrapReason?.name;
      },
    },
    {
      field: 'downtimeReasonCode',
      headerName: 'Leállás oka',
      flex: 1,
      valueGetter: (data) => {
        return data.row.downtimeReason?.name;
      },
    },
    {
      field: 'productionAttachment',
      headerName: 'Csatolmányok',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <ButtonUI
            size="sm"
            className="bg-success px-2.5 py-1"
            text={
              <Box display="flex" alignItems="center" gap={0.5}>
                <AttachFileIcon fontSize="small" />
                <Typography fontSize="medium">{params.row.productionAttachment?.length}</Typography>
              </Box>
            }
            onClick={() =>
              setReportAttachmentDialog({
                open: true,
                attachments: params.row.productionAttachment,
                productionId: params.row.productionId,
                productionReportId: params.row.id,
              })
            }
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Műveletek',
      flex: 1,
      getActions: ({ id, row }) => [
        <ActionButtonUI
          id={id}
          rowModesModel={rowModesModel}
          handleSaveClick={handleSaveClick}
          handleCancelClick={handleCancelClick}
          handleEditClick={handleEditClick}
          handleDeleteClick={handleDeleteClick}
          disabled={false}
        />,
        <History
          onClick={() => {
            setHistoryDialogOpen(true);
            setSelectedRow(row);
          }}
          fontSize="1.6rem"
        />,
      ],
    },
  ]);

  return (
    <div className="col-span-4 flex flex-col overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="col h-full flex-col overflow-hidden bg-white p-2 shadow sm:rounded-lg">
        <div className="flex h-full overflow-hidden border-t border-gray-200">
          <div className="w-full overflow-x-auto xxl:overflow-x-hidden">
            <div className="z-40 min-w-full h-[100%]">
              <DataGridUI
                datagridIdentifier={DataGridIdentifiers.OperationReportTable}
                rows={reports.rows}
                columns={columns}
                rowCount={reports.rowCount}
                sx={{ height: 520, width: '100%' }}
                getRowId={(row) => row.id}
                paginationMode="server"
                paginationModel={query.pagination.paginationModel}
                onPaginationModelChange={onPaginationChange}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
                processRowUpdate={processRowUpdate}
                editMode="row"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
